// app/src/features/session/useSessionWiring.js
import { useAuthSession } from "../../hooks/useAuthSession.js";

/**
 * The session wiring: `useAuthSession` — the signed-in user, the auth / TOS gate state, the
 * preferences pair and the server's feature flags — registered here as the FIRST hook of
 * `AppInner`, exactly where its destructure sat. One input, `setAuthEnabledFlag`: `App.jsx`'s
 * module-scope writer of the `__authEnabled` mirror the tour reads live; it is handed on verbatim
 * (the hook lists it in `fetchMe`'s array as a stable module-level function — never wrap it).
 *
 * Everything returned is read by `AppInner` under the same names: the thirty-one names the parent
 * destructured, one rename (`preprocessVariant` → `preprocessVariantDefault`) kept here, every
 * setter RAW (`setPreferences`, `setTos`, `setTosPreview` are `useAuthSession`'s own `useState`
 * setters, passed on unchanged — `useForecastSelection` takes `setPreferences` as an input, the
 * auth shells read `setTos`, the `tosPreview` overlay and `SettingsModal` read `setTosPreview`).
 * Nothing here reads the store or runs an effect; both of the hook's effects are its own. The
 * hook's `fetchMe` is not returned, as the parent never read it.
 */
export function useSessionWiring({ setAuthEnabledFlag }) {
  const {
    me,
    preferences,
    setPreferences,
    authEnabled,
    persistenceEnabled,
    _debugUIAllowed,
    debugMenuAllowed,
    preprocessVariant: preprocessVariantDefault,
    _forecastTrackingAllowed,
    notifyEmailEnabled,
    leadingBinAlertsEnabled,
    quickDeepToggleAllowed,
    quickDeepSelectorAllowed,
    signalSuggestionsEnabled,
    shiftVerdictEnabled,
    multiwayEnabled,
    autoQuestionTypeEnabled,
    allMultiwayEnabled,
    outcomeHistoryLinesEnabled,
    forecastLiveProgressEnabled,
    copyLinksEnabled,
    gcpProject,
    orgSharingEnabled,
    alternateSpacesEnabled,
    reportListenEnabled,
    dynamicReportEnabled,
    org,
    tos,
    setTos,
    tosPreview,
    setTosPreview,
    authLoading,
    guardForTos,
  } = useAuthSession({ setAuthEnabledFlag });

  return {
    me,
    preferences,
    setPreferences,
    authEnabled,
    persistenceEnabled,
    _debugUIAllowed,
    debugMenuAllowed,
    preprocessVariantDefault,
    _forecastTrackingAllowed,
    notifyEmailEnabled,
    leadingBinAlertsEnabled,
    quickDeepToggleAllowed,
    quickDeepSelectorAllowed,
    signalSuggestionsEnabled,
    shiftVerdictEnabled,
    multiwayEnabled,
    autoQuestionTypeEnabled,
    allMultiwayEnabled,
    outcomeHistoryLinesEnabled,
    forecastLiveProgressEnabled,
    copyLinksEnabled,
    gcpProject,
    orgSharingEnabled,
    alternateSpacesEnabled,
    reportListenEnabled,
    dynamicReportEnabled,
    org,
    tos,
    setTos,
    tosPreview,
    setTosPreview,
    authLoading,
    guardForTos,
  };
}
